import { node, text } from "~/util/dom";
import Scene from "~/scene";
import { store } from "~/state/store";
import { setCoordinates, setEgg, setMapset } from "~/state/map/state";
import { MAP_OVERLAYS } from "~/state/map/constants";
import createSDSIcon from "~/ui/icon";
import { CLOSE_24, CROWN_FILLED_16, PLUS_24 } from "@sznds/icons";
import { ICON_MAP_EDIT_24, ICON_M3D_24, ICON_MAPSET_24, ICON_PANORAMA_24, ICON_REMOVE_24, ICON_TOOLS_24 } from "~/ui/icons";
import { _ } from "~/i18n";
import MapsetMenu from "./mapsetmenu";
import ToolsMenu from "./toolsmenu";
import { watch, watchMap, watchMapEggs, watchPremiumData } from "~/state/util";
import { addListener } from "~/lib/signals";
import Tooltip from "~/ui/tooltip";
import { chmapLog } from "~/util/analyticsAction";
import { setMapsetUserSettings } from "~/util/providers/mapybox";
import { getMapsetBySEO } from "~/util/mapset";
import { CONFIG, getUserwebConfigData, setUserwebConfigData } from "~/config";
import ControlScale from "~/ui/controls/scale";
import Copyright from "~/ui/controls/copyright";
import ControlPointer from "~/ui/controls/pointer";
import { createMapyLogo } from "~/ui/controls/mapy-logo";
import { createGoToMapyCz } from "~/ui/controls/go-to-mapycz";
import { TRAVEL_24 } from "~/util/sds-compatible-icons";
import { openPropositionDialog } from "~/util/premium";
import { removeItem } from "~/util/cookies";
import { removeEgg, setEggVariant } from "~/egg";
import Closureswitch from "~/closureswitch";

const ALLOW_3D = ["zakladni", "turisticka", "letecka", "zimni"];

function showPanoramaButton(parent: HTMLMapyMapButtonGroupElement, button: HTMLMapyMapToggleElement, countryData: any, zoom: number) {
	const MIN_ZOOM = 7;

	if (zoom >= MIN_ZOOM && countryData.cs?.find && !Mapy.getComponent("distanceMeter").isActive()) {
		parent.appendChild(button);
	} else {
		button.remove();
	}
}

class MapControls {
	#initialized: boolean;

	#node: HTMLElement;

	#allControls: HTMLElement;

	#hudElements: Array<HTMLElement>;

	#zoomInButton: HTMLMapyMapButtonElement;

	#zoomOutButton: HTMLMapyMapButtonElement;

	/** prepinac 3D mapy */
	#m3dButton: HTMLMapyMapToggleElement;

	#panoramaButton: HTMLMapyMapToggleElement;

	#wasmButton: HTMLMapyMapToggleElement;

	/** prepinac "letadelko" */
	#ophotoButton: HTMLMapyMapToggleElement;

	/** madlo pro roztahovani sloupce */
	#resizer: HTMLButtonElement;

	/** tlacitko pro menu s mapsety */
	#mapsetButton: HTMLMapyMapToggleElement;

	/** tlacitko pro menu s nastroji */
	#toolsButton: HTMLMapyMapToggleElement;

	#tooltips: Array<Tooltip>;

	#scale: ControlScale;

	#copyright: Copyright;

	#pointers: Array<ControlPointer>;

	#waitingPointers: Array<ControlPointer>;

	#printLogoImg: HTMLImageElement;

	#oldMapset: string;

	#closeEggButton: HTMLMapyMapButtonElement;

	constructor() {
		const mapControls = document.getElementById("map-controls");

		if (mapControls) {
			this.#node = mapControls;
			this.#node.classList.add("noprint");
		} else {
			this.#node = node("div", { className: "map-controls noprint" });
		}

		this.#tooltips = [];
		this.#pointers = [];
		this.#hudElements = [];
		this.#waitingPointers = [];
		this.#initialized = false;
		this.#printLogoImg = null;
		this.#oldMapset = "zakladni";
		this.#allControls = document.getElementById("all-controls");
	}

	destroy() {
		this.#tooltips.forEach(tooltip => {
			tooltip.destroy();
		});
	}

	get node() {
		return this.#node;
	}

	init() {
		const state = store.getState();

		// horni panel
		const topToolbar = node("div", { className: "map-controls__topToolbar" });
		const leftTools = node("div", { className: "map-controls__topToolbar__leftTools" });
		const rightTools = node("div", { className: "map-controls__topToolbar__rightTools" });
		const leftToggleGroup = node("mapy-map-button-group", { className: "map-controls__leftToggleGroup" });
		const leftMapsetGroup = node("mapy-map-button-group", { className: "map-controls__leftMapsetGroup" });

		topToolbar.appendChild(leftTools);
		topToolbar.appendChild(rightTools);
		this.#node.appendChild(topToolbar);

		// dolni panel
		const bottomToolbar = node("div", { className: "map-controls__bottomToolbar" });

		// dolni panel vlevo
		const bottomLeftControls = node("div", { className: "bottom-left-controls" });

		this.#node.appendChild(bottomToolbar);

		// meritko a copyright
		this.#scale = new ControlScale();
		this.#scale.setCount(state.map.latitude, state.map.zoom, Scene.getSize().x);
		this.#scale.visible = state.map.provider === "map";
		this.#scale.setData(state.map.latitude, state.map.zoom);
		bottomLeftControls.append(this.#scale.container);

		this.#copyright = new Copyright();
		bottomLeftControls.append(this.#copyright.container);
		this.#node.append(bottomLeftControls);

		// ovladac m3d
		const m3dHandler = async () => {
			const { mapset, currentOverlays } = store.getState().map;

			// TODO: tuhle logiku přenést do state, kde zde se tedy jen nastaví m3d, případně rovnou s mapsetem a tam se už zajistí, že se nastaví správná kombinace
			this.#m3dButton.disabled = true;

			if (Scene.isM3dActive()) {
				await Scene.setActive("map");

				// analytika
				chmapLog({
					mapset: `${mapset}_3D`,
					mapsetNew: mapset,
					overlays: [],
					overlaysNew: mapset in currentOverlays ? [...currentOverlays[mapset]] : [],
					type: "3D",
				});

				const loader3d = Scene.providers.m3d && Scene.providers.m3d.getPresenter() ? Scene.providers.m3d.getPresenter().loader : null;

				if (loader3d) {
					loader3d.remove();
				}

				Mapy.getComponent("logger").log("closem3d");
			} else {
				// analytika
				chmapLog({
					mapsetNew: `${mapset}_3D`,
					overlays: mapset in currentOverlays ? [...currentOverlays[mapset]] : [],
					overlaysNew: [],
					type: "3D",
				});

				await Scene.setActive("m3d");
				Mapy.getComponent("logger").log("openm3d");
			}

			Mapy.getComponent("history").save();
			setTimeout(() => {
				this.#m3dButton.disabled = false;
			}, 1000);
		};

		// prepinac map
		let mapsetMenu: MapsetMenu = null;
		const openMapsetMenu = () => {
			Mapy.getComponent("logger").log("mapset_open");
			mapsetMenu = new MapsetMenu({
				anchor: this.#mapsetButton,
				onClose: () => {
					mapsetMenu?.destroy();
					mapsetMenu = null;
					this.#mapsetButton.checked = false;
				},
				onOpen: () => {
					this.#mapsetButton.checked = true;
				},
				position: "bottom-start",
				activator: this.#mapsetButton,
				clickOutClose: true,
			});
			setTimeout(() => {
				mapsetMenu?.open();
			}, 0);
		};

		this.#mapsetButton = node("mapy-map-toggle", { className: "map-controls__mapset" });
		this.#mapsetButton.appendChild(createSDSIcon({ symbol: ICON_MAPSET_24 }));
		this.#mapsetButton.appendChild(node("span", { className: "map-controls__text" }, _("mapsetSwitch.buttonText")));
		this.#mapsetButton.addEventListener("change", () => {
			if (this.#mapsetButton.checked) {
				openMapsetMenu();
			} else {
				mapsetMenu?.close();
			}
		}, false);

		// zavreni egg tlacitko
		this.#closeEggButton = node("mapy-map-button", { className: "map-controls__egg" });
		this.#closeEggButton.classList.add("hide");
		this.#closeEggButton.appendChild(createSDSIcon({ symbol: CLOSE_24 }));
		this.#closeEggButton.appendChild(node("span", { className: "map-controls__text" }, _("close")));
		this.#closeEggButton.addEventListener("click", () => {
			store.dispatch(setEgg("none"));
		});
		rightTools.append(this.#closeEggButton);

		// premium tlacitko
		const premiumButton = node("mapy-map-button", { className: "map-controls__premium" });
		const wtUserPremium = watch(store.getState, "user", watchPremiumData);

		premiumButton.setAttribute("data-log-action", "HMPGButtonPremium_premiumPurchase_show");
		premiumButton.append(createSDSIcon({ symbol: CROWN_FILLED_16 }));
		premiumButton.appendChild(node("span", { className: "map-controls__text" }, _("mapControls.premium")));
		premiumButton.addEventListener("click", () => {
			openPropositionDialog("HMPGButtonPremium");
		});

		if (state.map.mapset === "uzavirky") {
			rightTools.append(Closureswitch.container);
		}

		if (!state.user.user.premium && !CONFIG.inFrame) {
			rightTools.appendChild(premiumButton);
		}

		if (!CONFIG.inFrame) {
			store.subscribe(wtUserPremium(newUser => {
				if (newUser.user.premium) {
					premiumButton.remove();
				} else {
					rightTools.prepend(premiumButton);
				}
			}));
		}

		// geolokace
		const geolocation = Mapy.getComponent("geolocation");

		if (geolocation.isSupported()) {
			const button = geolocation.getButton();

			button.classList.add("map-controls__geolocation");
			rightTools.appendChild(button);
		}

		// zoom
		const zoomGroup = node("mapy-map-button-group");
		const [minZoom, maxZoom] = Scene.getZoomRange();
		const zoomIn = node("span", { className: "map-controls__buttonBox" });
		const zoomOut = node("span", { className: "map-controls__buttonBox" });

		this.#zoomInButton = node("mapy-map-button");
		this.#zoomInButton.appendChild(createSDSIcon({ symbol: PLUS_24 }));
		this.#zoomInButton.addEventListener("click", this, false);
		zoomIn.appendChild(this.#zoomInButton);
		this.#zoomOutButton = node("mapy-map-button");
		this.#zoomOutButton.appendChild(createSDSIcon({ symbol: ICON_REMOVE_24 }));
		this.#zoomOutButton.addEventListener("click", this, false);
		zoomOut.appendChild(this.#zoomOutButton);
		zoomGroup.appendChild(zoomOut);
		zoomGroup.appendChild(zoomIn);
		rightTools.appendChild(zoomGroup);
		this.#zoomInButton.disabled = state.map.zoom === maxZoom;
		this.#zoomOutButton.disabled = state.map.zoom === minZoom;

		// resizer
		this.#resizer = node("button", { type: "button", className: "map-controls__resizer noprint" });
		this.#resizer.addEventListener("click", () => {
			const layout = Mapy.getComponent("layout");
			let width = layout.getWidth();

			switch (width) {
				case 1:
					width = 0;
					break;
				case 0:
					width = 1;
					break;
				case 2:
					width = 1;
					break;
				case 3:
					width = 2;
					break;
				default:
			}

			layout.setWidth(width);
			Mapy.getComponent("history")?.save();
		}, false);
		window.addEventListener("keyup", event => {
			const layout = Mapy.getComponent("layout");

			if (layout.getWidth() === 3 && event.code === "Escape") {
				layout.setWidth(2);
				Mapy.getComponent("history")?.save();
			}
		}, false);

		if (CONFIG.inFrame) {
			this.#allControls.appendChild(createGoToMapyCz());

			if (state.map.provider === "m3d") {
				document.body.classList.add("m3d-iframe");
			}
		} else {
			this.#allControls.appendChild(this.#resizer);

			// mapove logo
			this.#allControls.append(createMapyLogo({
				large: true,
				hidden: true,
			}));
		}

		// 3D
		this.#m3dButton = node("mapy-map-toggle", { className: "map-controls__m3d", checked: Scene.isM3dActive() });
		this.#m3dButton.appendChild(createSDSIcon({ symbol: ICON_M3D_24 }));
		this.#m3dButton.appendChild(node("span", { innerText: _("layerControl.m3d"), className: "map-controls__text" }));
		this.#m3dButton.addEventListener("change", m3dHandler, false);
		this.#m3dButton.disabled = ALLOW_3D.indexOf(state.map.mapset) === -1;

		// panorama
		this.#panoramaButton = node("mapy-map-toggle", { className: "map-controls__panorama", checked: state.map.overlays.indexOf(MAP_OVERLAYS.PANORAMA) !== -1 });
		this.#panoramaButton.appendChild(createSDSIcon({ symbol: ICON_PANORAMA_24 }));
		this.#panoramaButton.appendChild(node("span", { innerText: _("layerControl.panorama"), className: "map-controls__text" }));
		this.#panoramaButton.addEventListener("change", this, false);

		// wasm
		this.#wasmButton = node("mapy-map-toggle", { className: "map-controls__wasm", checked: true });
		this.#wasmButton.appendChild(createSDSIcon({ symbol: ICON_MAP_EDIT_24 }));
		this.#wasmButton.appendChild(node("span", { innerText: "WASM", className: "map-controls__text" }));
		this.#wasmButton.addEventListener("click", this, false);

		// prepinac "letadelko"
		this.#ophotoButton = node("mapy-map-toggle", { className: "map-controls__ophoto", checked: state.map.mapset === "letecka" });
		this.#ophotoButton.appendChild(createSDSIcon({ symbol: TRAVEL_24 }));
		this.#ophotoButton.appendChild(node("span", { innerText: _("mapControls.ophoto"), className: "map-controls__text" }));
		this.#ophotoButton.addEventListener("change", this, false);

		// nastroje
		let toolsMenu: ToolsMenu = null;
		const openToolsMenu = () => {
			Mapy.getComponent("logger").log("tools_open");
			toolsMenu = new ToolsMenu({
				anchor: this.#toolsButton,
				onClose: () => {
					toolsMenu?.destroy();
					toolsMenu = null;
					this.#toolsButton.checked = false;
				},
				onOpen: () => {
					this.#toolsButton.checked = true;
				},
				position: "top-end",
				activator: this.#toolsButton,
			});
			setTimeout(() => {
				toolsMenu.open();
			}, 0);
		};

		this.#toolsButton = node("mapy-map-toggle", { className: "map-controls__tools" });
		this.#toolsButton.appendChild(createSDSIcon({ symbol: ICON_TOOLS_24 }));
		this.#toolsButton.appendChild(text(_("darthControl.tools")));
		this.#toolsButton.addEventListener("change", () => {
			if (this.#toolsButton.checked) {
				openToolsMenu();
			} else {
				toolsMenu?.close();
			}
		}, false);

		// finalni sestaveni UI
		if (!CONFIG.inFrame) {
			leftMapsetGroup.appendChild(this.#mapsetButton);
			leftMapsetGroup.appendChild(this.#ophotoButton);
			leftToggleGroup.appendChild(this.#m3dButton);

			if (CONFIG.wasmEnabled && location.href.includes("dev.dszn.cz")) {
				leftToggleGroup.appendChild(this.#wasmButton);
			}

			if (state.map.provider === "map") {
				showPanoramaButton(leftToggleGroup, this.#panoramaButton, Mapy.getComponent("statearea").checkStates(), state.map.zoom);
			}

			leftTools.appendChild(leftMapsetGroup);
			leftTools.appendChild(leftToggleGroup);
			bottomToolbar.appendChild(this.#toolsButton);
		}

		// pridame tooltipy
		this.#tooltips = [
			...geolocation.isSupported() ? [new Tooltip({ margin: [16, 16, 16, 16], anchor: geolocation.getButton(), content: node("span", {}, _("geolocation.base")), hideTimeout: 0 })] : [],
			new Tooltip({ margin: [16, 16, 16, 16], anchor: zoomIn, content: node("span", {}, _("zoomMenu.zoomIn")), hideTimeout: 0 }),
			new Tooltip({ margin: [16, 16, 16, 16], anchor: zoomOut, content: node("span", {}, _("zoomMenu.zoomOut")), hideTimeout: 0 }),
		];

		// sledovani zmen mapy, aby se podle toho menilo UI
		const wt = watch(store.getState, "map", watchMap);

		store.subscribe(wt((newMapState, oldMapState) => {
			// viditelnost prepinace panoramat podle stavu 3D a stav 3D tlacitka
			if (newMapState.provider !== oldMapState.provider) {
				this.#m3dButton.checked = newMapState.provider === "m3d";

				if (newMapState.provider === "map") {
					showPanoramaButton(leftToggleGroup, this.#panoramaButton, Mapy.getComponent("statearea").checkStates(), newMapState.zoom);
				} else {
					this.#panoramaButton.remove();
				}

				this.#scale.visible = newMapState.provider === "map";
			}

			// zakazani/povoleni 3D podle mapsetu
			if (newMapState.mapset !== oldMapState.mapset) {
				this.#m3dButton.disabled = ALLOW_3D.indexOf(newMapState.mapset) === -1;
				this.#ophotoButton.checked = newMapState.mapset === "letecka";

				if (newMapState.mapset === "letecka") {
					this.#oldMapset = oldMapState.mapset;
				}
			}

			// zakazovani a povolovani zoom tlacitek podle toho, na jakem jsme zoomu
			if (newMapState.zoom !== oldMapState.zoom) {
				const [min, max] = Scene.getZoomRange();

				// v pripade potreby zakazeme/povolime prislusna tlacitka pro zoom
				this.#zoomInButton.disabled = newMapState.zoom === max;
				this.#zoomOutButton.disabled = newMapState.zoom === min;
			}

			// checknuti panorama tlacitka podle toho, zda je zrovna aktivni
			const oldPanorama = oldMapState ? oldMapState.mapset in oldMapState.currentOverlays && oldMapState.currentOverlays[oldMapState.mapset].indexOf(MAP_OVERLAYS.PANORAMA) !== -1 : false;
			const newPanorama = newMapState.mapset in newMapState.currentOverlays && newMapState.currentOverlays[newMapState.mapset].indexOf(MAP_OVERLAYS.PANORAMA) !== -1;

			if (oldPanorama !== newPanorama) {
				this.#panoramaButton.checked = newPanorama;
			}

			this.#scale.setData(newMapState.latitude, newMapState.zoom);
			this.#pointers.forEach(pointer => pointer.redraw());
		}));

		if (!CONFIG.inFrame) {
			addListener(null, "state-area-check", (event: any) => {
				const { zoom } = store.getState().map;

				showPanoramaButton(leftToggleGroup, this.#panoramaButton, event.data || {}, zoom);
			});
			addListener(null, "distancemeter-activated", () => {
				this.#panoramaButton.remove();
			});
			addListener(null, "distancemeter-deactivated", () => {
				const { zoom } = store.getState().map;

				// potrebujeme opozdene, protoze pri vyvolani "distancemeter-deactivated" je distancemeter stale jeste ve stavu active
				// timpadem uvnitr funkce `showPanoramaButton` se podminka `!Mapy.getComponent("distanceMeter").isActive()` vyhodnoti
				// jako false a nedojde k pridani buttonu
				setTimeout(() => {
					showPanoramaButton(leftToggleGroup, this.#panoramaButton, Mapy.getComponent("statearea").checkStates(), zoom);
				}, 0);
			});

			// prepinani mapsetu pomoci klavesovych zkratek (par zakladnich, u kterych nehrozi, ze jen tak zmizi)
			document.addEventListener("keydown", (event: KeyboardEvent) => {
				if (event.ctrlKey && event.shiftKey) {
					// budeme menit mapset a tedy musime vynutit zavreni pripadne otevreneho menu a navic zakazeme na tu zkratku neco delat prohlizeci
					if (["Digit1", "Digit2", "Digit3", "Digit4", "Digit5"].indexOf(event.code) !== -1) {
						event.preventDefault();
						mapsetMenu?.close();

						switch (event.code) {
							case "Digit1":
								store.dispatch(setMapset({ mapset: "zakladni" }));
								break;

							case "Digit2":
								store.dispatch(setMapset({ mapset: "turisticka" }));
								break;

							case "Digit3":
								store.dispatch(setMapset({ mapset: "letecka" }));
								break;

							case "Digit4":
								store.dispatch(setMapset({ mapset: "dopravni" }));
								break;

							case "Digit5":
								store.dispatch(setMapset({ mapset: "zimni" }));
								break;

							default:
								//
						}

						Mapy.getComponent("history").save();

						const { mapset, overlays } = store.getState().map;

						setMapsetUserSettings(getMapsetBySEO(mapset)?.id, overlays.filter(overlay => overlay !== MAP_OVERLAYS.PANORAMA));
					}
				}
			});
		}

		const wtEggs = watch(store.getState, "map", watchMapEggs);

		store.subscribe(wtEggs(mapState => {
			if (mapState.egg === "none") {
				this.#closeEggButton.classList.add("hide");
				removeEgg();
			} else {
				if (!CONFIG.inFrame) {
					this.#closeEggButton.classList.remove("hide");
				}

				setEggVariant(mapState.egg);
			}
		}));

		if (state.map.egg !== "none") {
			this.#closeEggButton.classList.remove("hide");
		}

		// hud elementy
		this.#hudElements.push(leftTools, rightTools, bottomLeftControls, this.#toolsButton, this.#resizer);

		addListener(this, "resize", () => {
			this.syncPortPointers();
		});
		addListener(this, "width-change", () => {
			this.syncPortPointers();
		});
		addListener(this, "print-page", () => {
			this.#printLogoImg = createMapyLogo({
				hidden: false,
				large: true,
			});
			this.#allControls.append(this.#printLogoImg);
		});
		addListener(this, "print-done", () => {
			if (this.#printLogoImg) {
				this.#printLogoImg.remove();
				this.#printLogoImg = null;
			}
		});

		this.#initialized = true;
		// cekajici kapicky
		this.#waitingPointers.forEach(pointer => {
			this.addControlPointer(pointer);
		});
		this.#waitingPointers.length = 0;
	}

	syncPortPointers() {
		const size = this._getSize();

		this.#pointers.forEach(pointer => {
			pointer.setDimension(size.x, size.y);
			pointer.redraw();
		});
	}

	handleEvent(event: PointerEvent) {
		// zoom in a zoom out
		if (event.target === this.#zoomInButton || event.target === this.#zoomOutButton) {
			const { zoom } = store.getState().map;
			const [min, max] = Scene.getZoomRange();

			Mapy.getComponent("logger").log(event.target === this.#zoomInButton ? "zoom-in" : "zoom-out");

			// zmenime mapu
			store.dispatch(setCoordinates({
				zoom: event.target === this.#zoomInButton ? Math.min(zoom + 1, max) : Math.max(zoom - 1, min),
				animate: true,
			}));
			Mapy.getComponent("history").save();
		// panorama
		} else if (event.target === this.#panoramaButton) {
			const state = store.getState().map;
			const index = state.overlays.indexOf(MAP_OVERLAYS.PANORAMA);
			const overlays = [...state.overlays];

			if (index > -1) {
				overlays.splice(index, 1);
			}

			if (this.#panoramaButton.checked) {
				overlays.push(MAP_OVERLAYS.PANORAMA);
			}

			// analytika
			chmapLog({
				mapsetNew: state.mapset,
				overlays: state.mapset in state.currentOverlays ? [...state.currentOverlays[state.mapset]] : [],
				overlaysNew: overlays.sort(),
				...this.#panoramaButton.checked ? { overlayAdd: MAP_OVERLAYS.PANORAMA } : { overlayRemove: MAP_OVERLAYS.PANORAMA },
			});

			store.dispatch(setMapset({
				mapset: state.mapset,
				overlays,
			}));
			Mapy.getComponent("history").save();
		// letecka
		} else if (event.target === this.#ophotoButton) {
			const state = store.getState().map;
			const mapset = state.mapset === "letecka" ? this.#oldMapset : "letecka";
			// bez pripadnych panoramat
			const overlays: Array<MAP_OVERLAYS> = (mapset in state.currentOverlays ? [...state.currentOverlays[mapset]] : []).filter(overlay => overlay !== MAP_OVERLAYS.PANORAMA);

			// pridame panorama, pokud je aktivni
			if (state.overlays.indexOf(MAP_OVERLAYS.PANORAMA) !== -1) {
				overlays.push(MAP_OVERLAYS.PANORAMA);
			}

			store.dispatch(setMapset({
				mapset,
				overlays,
			}));
			setMapsetUserSettings(getMapsetBySEO(mapset)?.id, overlays.filter(overlay => overlay !== MAP_OVERLAYS.PANORAMA));
			Mapy.getComponent("history").save();
		} else if (event.target === this.#wasmButton) {
			removeItem(CONFIG.wasmCookie);

			const storage = getUserwebConfigData();

			if (storage?.userweb?.wasm) {
				storage.userweb.wasm = 0;
				setUserwebConfigData(storage);
			}

			location.reload();
		}
	}

	addControlPointer(pointer: ControlPointer) {
		if (!this.#initialized) {
			this.#waitingPointers.push(pointer);

			return;
		}

		const size = this._getSize();

		this.#pointers.push(pointer);
		pointer.setDimension(size.x, size.y);
		pointer.setHUDElems(this.#hudElements);
		this.#node.append(pointer.container);
	}

	removeControlPointer(pointer: ControlPointer) {
		if (!this.#initialized) {
			const ind = this.#waitingPointers.findIndex(item => item === pointer);

			if (ind !== -1) {
				this.#waitingPointers.splice(ind, 1);
			}

			return;
		}

		const ind = this.#pointers.findIndex(item => item === pointer);

		if (ind !== -1) {
			pointer.container.remove();
			this.#pointers.splice(ind, 1);
		}
	}

	moveResizerToMapControls() {
		this.#node.appendChild(this.#resizer);
	}

	_getSize() {
		return {
			x: this.#node.offsetWidth,
			y: this.#node.offsetHeight,
		};
	}
}

export default new MapControls();
